import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { fetchVendorsListData } from "../../Redux/features/VendorList/vListSlice";
import { useSelector, useDispatch } from "react-redux";
import { BASE_URL, ADD_VENDOR_DATA } from "../../Constants/Config";
import BasicTextFields from "../../reuseableComponents/TextInputField";
import { CircularProgress, Grid } from "@mui/material";
import SelectDropDown from "../../reuseableComponents/SelectDropDown";
import { useAuthDetails } from "../../Common/cookiesHelper";
import PasswordShow from "../../Common/passwordShow";
import { Link, useNavigate } from "react-router-dom";
import { ToastifyAlert } from "../../CommonComponents/ToastifyAlert";
import SwitchToBackButton from "../../reuseableComponents/SwitchToBackButton";
import { checkDuplicateEmailValidation } from "../../Redux/features/Reports/VendorList/VendorListSlice";
import useDebounce from "../../hooks/useDebouncs";
import { formatPhoneNumber } from "../../helperFunctions/formatPhoneNumber";
import { sanitizeName, wordsWithInBetweenSpace } from "../../Constants/utils";
import { PERMISSIONS } from "../../Constants/permission";
import AccessDeniedTooltip from "../../reuseableComponents/AccessDeniedTooltip";

const AddVendors = ({ setVisible }) => {
  const navigate = useNavigate();
  const { handleCoockieExpire, getUnAutherisedTokenMessage, getNetworkError } =
    PasswordShow();
  const { validationLoading } = useSelector((state) => state.VendorList);
  const { permissions = [] } = useSelector((state) => state.permissions || {});
  const [states, setStates] = useState([]);
  const { LoginGetDashBoardRecordJson, userTypeData } = useAuthDetails();
  let merchant_id = LoginGetDashBoardRecordJson?.data?.merchant_id;
  const AllVendorsDataState = useSelector((state) => state.vendors);
  const [errorMessage, setErrorMessage] = useState("");
  const [value, setValue] = useState();
  const [loader, setLoader] = useState(false);
  const hasAddVendorPermission =
    !permissions.length || permissions.includes(PERMISSIONS.MENU_ADD_VENDOR);
  const [vendor, setVendor] = useState({
    name: "",
    // email: "",
    phone: "",
    full_address: "",
    city: "",
    zip_code: "",
    state: "",
  });

  const [vendorEmail, setVendorEmail] = useState("");
  const skipValidationRef = useRef(false);
  const debouncedValue = useDebounce(vendorEmail, 500);

  const dispatch = useDispatch();

  useEffect(() => {
    getVendorListData();
  }, []);

  const getVendorListData = async () => {
    try {
      let data = {
        merchant_id,
      };
      await dispatch(fetchVendorsListData(data)).unwrap();
    } catch (error) {
      if (error?.status == 401 || error?.response?.status === 401) {
        getUnAutherisedTokenMessage();
        handleCoockieExpire();
      } else if (error?.status == "Network Error") {
        getNetworkError();
      }
    }
  };

  const validate = async () => {
    let isValid = true;
    const itemErrors = {};
    if (!vendor.name) {
      itemErrors.name = "Please enter a vendor name";
      isValid = false;
    } else if (!wordsWithInBetweenSpace.test(vendor.name.trim())) {
      itemErrors.name = "Special characters are not allowed";
      isValid = false;
    } else if (vendor.name.trim().length > 50) {
      itemErrors.name = "Vendor Name cannot exceed 50 characters";
      isValid = false;
    }
    if (
      vendorEmail !== "" &&
      !/^(?!.*\.\.)[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
        vendorEmail
      )
    ) {
      itemErrors.email = "Invalid email address";
      isValid = false;
    }
    if (vendorEmail && /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(vendorEmail)) {
      const isValidMail = await checkEmailValidation(vendorEmail, "submit");
      if (!isValidMail) {
        itemErrors.email = "Email id already exist";
        isValid = false;
      }
    }
    if (!/^\d{10}$/.test(vendor.phone)) {
      itemErrors.phone = "Phone number must be 10 digits";
      isValid = false;
    }
    // if (!vendor.full_address) {
    //   itemErrors.full_address = "Please enter a vendor address";
    //   isValid = false;
    // }
    // if (!vendor.city) {
    //   itemErrors.city = "Please enter a vendor city";
    //   isValid = false;
    // }
    if (vendor.zip_code !== "" && !/^\d{5}$/.test(vendor.zip_code)) {
      itemErrors.zip_code = "ZIP code must be 5 digits";
      isValid = false;
    }
    // if (!vendor.state) {
    //   itemErrors.state = "Please select state";
    //   isValid = false;
    // }
    setErrorMessage(itemErrors);
    return isValid;
  };

  const checkEmailValidation = async (value, type) => {
    const payload = {
      merchant_id,
      email: value,
      ...userTypeData,
    };

    const data = await dispatch(checkDuplicateEmailValidation(payload));
    const isValid = data?.payload;

    if (type === "submit" && value) {
      return isValid;
    } else {
      setErrorMessage((prev) => ({
        ...prev,
        email: isValid || isValid === undefined ? "" : "Email id already exist",
      }));
    }
  };

  // Run on query change
  useEffect(() => {
    if (skipValidationRef.current) return;
    const emailRegex = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/;

    if (emailRegex.test(debouncedValue)) {
      checkEmailValidation(debouncedValue);
    }
  }, [debouncedValue, skipValidationRef]);

  const handleEmailChange = async (e) => {
    skipValidationRef.current = false;
    const { name, value } = e.target;
    let errorMsg = "";
    if (value == "") {
      errorMsg = "";
    } else if (
      !/^(?!.*\.\.)[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
    ) {
      errorMsg = "Invalid email address";
    }

    setErrorMessage((prevState) => ({
      ...prevState,
      [name]: errorMsg,
    }));
    setVendorEmail(value);
  };

  const inputChange = (e) => {
    const { name, value } = e.target;
    skipValidationRef.current = false;

    let errorMsg = "";
    switch (name) {
      case "name":
        if (value.length <= 0) {
          errorMsg = "Please enter a vendor name";
        } else if (!/[a-zA-Z]/.test(value)) {
          errorMsg = "Please enter atleast one character";
        }
        break;

      case "phone":
        const numbersOnly = value?.replace(/\D/g, "");
        if (!/^\d{10}$/.test(numbersOnly)) {
          errorMsg = "Phone number must be 10 digits";
        }
        break;
      // case "zip_code":
      //   if (!/^\d{5}$/.test(value)) {
      //     errorMsg = "ZIP code must be 5 digits";
      //   }
      //   break;
      // case "full_address":
      //   if (value.length <= 0) {
      //     errorMsg = "Please enter a vendor address";
      //   }
      //   break;
      // case "city":
      //   if (value.length <= 0) {
      //     errorMsg = "Please enter a vendor city";
      //   }
      //   break;
      // case "state":
      //   if (vendor.state.length <= 0) {
      //     errorMsg = "Please select state";
      //   }
      //   break;
      default:
        break;
    }
    setErrorMessage((prevState) => ({
      ...prevState,
      [name]: errorMsg,
    }));
    if (name != "phone") {
      setVendor((preValue) => {
        return {
          ...preValue,
          [name]: value,
        };
      });
    } else {
      const numbersOnly = value?.replace(/\D/g, "");
      setVendor((preValue) => {
        return {
          ...preValue,
          [name]: numbersOnly,
        };
      });
    }
  };

  useEffect(() => {
    if (
      !AllVendorsDataState.loading &&
      AllVendorsDataState.vendorListData &&
      AllVendorsDataState.vendorListData.length >= 1
    ) {
      const stateArray = AllVendorsDataState.vendorListData[2].map(
        (item) => item.State
      );
      setStates(stateArray);
    }
  }, [
    AllVendorsDataState,
    AllVendorsDataState.loading,
    AllVendorsDataState.vendorListData,
  ]);

  const handleSetVendorStateChange = (newState) => {
    setErrorMessage((prevState) => ({
      ...prevState,
      ["state"]: "",
    }));
    setVendor((preState) => ({
      ...preState,
      state: newState["title"],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    skipValidationRef.current = false;
    const isValid = await validate();
    if (isValid) {
      try {
        setLoader(true);
        // Assuming `vendor` is an object that you want to send in the request
        let updatedVendor = {
          ...vendor,
          ...userTypeData,
          email: vendorEmail?.trim() || "",
          merchant_id,
          login_employee_id: LoginGetDashBoardRecordJson?.employee_id
            ? LoginGetDashBoardRecordJson?.employee_id
            : null,
        };
        const { token, ...newData } = updatedVendor;
        const response = await axios.post(BASE_URL + ADD_VENDOR_DATA, newData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response) {
          navigate("/inventory/old-vendors");
          ToastifyAlert("Added Successfully", "success");
        } else {
          console.error(response);
        }
      } catch (error) {
        if (error?.status == 401 || error?.response?.status === 401) {
          getUnAutherisedTokenMessage();
          handleCoockieExpire();
        } else if (error?.status == "Network Error") {
          getNetworkError();
        }
      } finally {
        setLoader(false);
      }
    }
  };

  const handleSubmitWithPermission = (e) => {
    if (!hasAddVendorPermission) {
      e.preventDefault();
      return;
    }
    handleSubmit(e);
  };

  const handleKeyPress = (e) => {
    if ((e.charCode < 48 || e.charCode > 57) && e.charCode !== 8) {
      e.preventDefault();
    }
  };

  const handleName = (e) => {
    const name = sanitizeName(e.target.value);
    setVendor((prev) => ({ ...prev, name }));
  };

  return (
    <>
      <div className="mt-6">
        <Grid container className="box">
          <Grid item xs={12} className="q-add-categories-section">
            <Grid container>
              <SwitchToBackButton
                linkTo={"/inventory/old-vendors"}
                title={"Add New Vendor"}
              />
            </Grid>
            <Grid container sx={{ py: 2.5, mb: { xs: 15, sm: 0 } }}>
              <Grid item xs={12} sx={{ px: { xs: 1, sm: 2.5 } }}>
                <form onSubmit={handleSubmitWithPermission}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <div className=" qvrowmain my-1">
                        <label htmlFor="vendorName">Vendor Name</label>
                      </div>
                      <BasicTextFields
                        type={"text"}
                        name="name"
                        value={vendor.name}
                        placeholder="Name"
                        onChangeFun={handleName}
                        maxLength={50}
                      />
                      {errorMessage.name && (
                        <span className="error">{errorMessage.name}</span>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <div className=" qvrowmain my-1">
                        <label htmlFor="email">Email Address</label>
                      </div>
                      <BasicTextFields
                        type={"email"}
                        name="email"
                        value={vendorEmail}
                        placeholder="Email"
                        onChangeFun={handleEmailChange}
                      />
                      {errorMessage.email && (
                        <span className="error">{errorMessage.email}</span>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <div className="qvrowmain my-1">
                        <label htmlFor="phone">Phone Number</label>
                      </div>
                      <BasicTextFields
                        type={"text"}
                        placeholder="Phone"
                        // required={"required"}
                        name={"phone"}
                        onChangeFun={inputChange}
                        value={formatPhoneNumber(vendor.phone)}
                        maxLength={14}
                        onKeyPressFun={handleKeyPress}
                      />
                      {errorMessage.phone && (
                        <span className="error">{errorMessage.phone}</span>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container sx={{ marginTop: 0 }} spacing={2}>
                    <Grid item xs={12}>
                      <div className=" qvrowmain my-1">
                        <label htmlFor="address">Address</label>
                      </div>
                      <BasicTextFields
                        type={"text"}
                        name={"full_address"}
                        placeholder="Address Line 1"
                        onChangeFun={inputChange}
                        value={vendor.full_address}
                      />
                      {errorMessage.full_address && (
                        <span className="error">
                          {errorMessage.full_address}
                        </span>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container sx={{ marginTop: 0 }} spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <div className="qvrowmain my-1">
                        <label htmlFor="city">City</label>
                      </div>
                      <BasicTextFields
                        type={"text"}
                        name={"city"}
                        value={vendor.city}
                        placeholder="City"
                        onChangeFun={inputChange}
                      />
                      {errorMessage.city && (
                        <span className="error">{errorMessage.city}</span>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <div className="my-1 qvrowmain">
                        <label htmlFor="zip">Zip</label>
                      </div>
                      <BasicTextFields
                        type={"text"}
                        name="zip_code"
                        value={vendor.zip_code}
                        placeholder="Zip"
                        onChangeFun={inputChange}
                        maxLength={5}
                        onKeyPressFun={handleKeyPress}
                      />
                      {errorMessage.zip_code && (
                        <span className="error"> {errorMessage.zip_code}</span>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <div className="my-1 qvrowmain">
                        <label htmlFor="State">State</label>
                      </div>
                      <SelectDropDown
                        listItem={states.map((item) => ({
                          title: item,
                          name: "state",
                          value: vendor.state,
                        }))}
                        title={"title"}
                        selectedOption={vendor.state}
                        onClickHandler={handleSetVendorStateChange}
                        name={"state"}
                      />
                      {errorMessage.state && (
                        <span className="error">{errorMessage.state}</span>
                      )}
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    xs={12}
                    // sx={{  }}
                    sx={{
                      marginTop: 3,
                      mb: 0,
                      p: { xs: 2.5, sm: 0 },
                      width: "100%",
                      height: "auto",
                      boxShadow: { xs: "0 5px 10px 1px #ddd", sm: "unset" },
                      mx: { xs: "-10px", sm: "0px" },
                      borderRadius: "8px",
                      backgroundColor: "var(--qv-bg-ffffff, #fff)",
                      // padding: "0px",
                      overflow: "hidden",
                      position: { xs: "fixed", sm: "relative" },
                      bottom: { xs: 0, sm: "unset" },
                    }}
                  >
                    <AccessDeniedTooltip hasPermission={hasAddVendorPermission}>
                      <span>
                        <button
                          type="submit"
                          className="quic-btn quic-btn-save me-3 w-40"
                          disabled={loader || validationLoading}
                        >
                          {loader ? (
                            <CircularProgress
                              color={"inherit"}
                              className=""
                              width={15}
                              size={15}
                            />
                          ) : (
                            "Add"
                          )}
                        </button>
                      </span>
                    </AccessDeniedTooltip>
                    <Link to={`/inventory/old-vendors`}>
                      <button
                        // onClick={() => setVisible("VendorsDetail")}
                        className="quic-btn quic-btn-cancle"
                      >
                        Cancel
                      </button>
                    </Link>
                  </Grid>
                </form>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default AddVendors;
